import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Tooltip,
  CircularProgress,
  Stack,
  Divider,
  Avatar,
  Chip,
  Menu
} from "@mui/material";
import {
  SaveAlt,
  History,
  Forward,
  CalendarToday,
  KeyboardArrowDown
} from "@mui/icons-material";
import Pagination from "@mui/material/Pagination";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";
import Autocomplete from "@mui/material/Autocomplete";
import * as XLSX from 'xlsx';

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { UsersFilter } from "../../components/UsersFilter";
import { WhatsappsFilter } from "../../components/WhatsappsFilter";
import { StatusFilter } from "../../components/StatusFilter";
import useDashboard from "../../hooks/useDashboard";
import QueueSelectCustom from "../../components/QueueSelectCustom";
import moment from "moment";
import ShowTicketLogModal from "../../components/ShowTicketLogModal";

// ---------------------------------------------------------------------------
// DateRangePicker Component
// ---------------------------------------------------------------------------
const DateRangePicker = ({ dateRange, onDateRangeChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(dateRange.startDate);
  const [endDate, setEndDate] = useState(dateRange.endDate);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectingStartDate, setSelectingStartDate] = useState(true);

  useEffect(() => {
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
  }, [dateRange]);

  const formatDateDisplay = (date) => {
    const months = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
                      "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
  const dayNames = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  const handleClick = (event) => { setAnchorEl(event.currentTarget); };
  const handleClose = () => { setAnchorEl(null); };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek };
  };

  const handleDateClick = (day) => {
    const selectedDate = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
    if (selectingStartDate) {
      setStartDate(selectedDate);
      if (selectedDate > endDate) setEndDate(selectedDate);
      setSelectingStartDate(false);
    } else {
      setEndDate(selectedDate);
      if (selectedDate < startDate) setStartDate(selectedDate);
      setSelectingStartDate(true);
    }
  };

  const isDateInRange = (day) => {
    const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
    return date >= startDate && date <= endDate;
  };

  const isSameDay = (day, compareDate) => {
    const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
    return date.getDate() === compareDate.getDate() &&
           date.getMonth() === compareDate.getMonth() &&
           date.getFullYear() === compareDate.getFullYear();
  };

  const handlePrevMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
  };
  
  const handleNextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));
  };

  const handleApplyDates = () => {
    if (onDateRangeChange) onDateRangeChange({ startDate, endDate });
    handleClose();
  };

  const handleClearDates = () => {
    const newStartDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const newEndDate = new Date();
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setCalendarMonth(new Date());
    if (onDateRangeChange) onDateRangeChange({ startDate: newStartDate, endDate: newEndDate });
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(calendarMonth);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <Box>
      <Button
        onClick={handleClick}
        startIcon={<CalendarToday sx={{ fontSize: 18 }} />}
        sx={{
          bgcolor: "#fff",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          px: 3,
          py: 1.25,
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          textTransform: "none",
          fontWeight: 600,
          color: "text.primary",
          fontSize: "0.875rem",
          "&:hover": { 
            bgcolor: "action.hover",
            borderColor: "primary.main"
          }
        }}
      >
        {formatDateDisplay(startDate)} - {formatDateDisplay(endDate)}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            mt: 1,
            minWidth: 380,
            maxWidth: 420,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            p: 3,
            border: "1px solid",
            borderColor: "divider"
          }
        }}
      >
        <Box>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", mb: 1, display: "block", color: "text.secondary", fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                Data Início
              </Typography>
              <Button
                fullWidth
                onClick={() => setSelectingStartDate(true)}
                sx={{
                  py: 1.5,
                  justifyContent: "flex-start",
                  borderRadius: 2,
                  bgcolor: selectingStartDate ? "primary.main" : "action.hover",
                  color: selectingStartDate ? "primary.contrastText" : "text.primary",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  "&:hover": { bgcolor: selectingStartDate ? "primary.dark" : "action.selected" }
                }}
              >
                {formatDateDisplay(startDate)}
              </Button>
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-end", pb: 1 }}>
              <Typography variant="h6" sx={{ color: "text.secondary" }}>→</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", mb: 1, display: "block", color: "text.secondary", fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                Data Fim
              </Typography>
              <Button
                fullWidth
                onClick={() => setSelectingStartDate(false)}
                sx={{
                  py: 1.5,
                  justifyContent: "flex-start",
                  borderRadius: 2,
                  bgcolor: !selectingStartDate ? "primary.main" : "action.hover",
                  color: !selectingStartDate ? "primary.contrastText" : "text.primary",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  "&:hover": { bgcolor: !selectingStartDate ? "primary.dark" : "action.selected" }
                }}
              >
                {formatDateDisplay(endDate)}
              </Button>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <IconButton onClick={handlePrevMonth} size="small">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </IconButton>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: "0.875rem" }}>
              {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
            </Typography>
            <IconButton onClick={handleNextMonth} size="small">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </IconButton>
          </Stack>

          <Grid container spacing={0.5} sx={{ mb: 1 }}>
            {dayNames.map((day) => (
              <Grid item xs={12/7} key={day}>
                <Typography variant="caption" align="center" display="block" sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.7rem" }}>
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={0.5} sx={{ mb: 3 }}>
            {Array.from({ length: startingDayOfWeek }).map((_, index) => (
              <Grid item xs={12/7} key={`empty-${index}`}>
                <Box sx={{ height: 36 }} />
              </Grid>
            ))}
            {days.map((day) => {
              const isStart = isSameDay(day, startDate);
              const isEnd = isSameDay(day, endDate);
              const inRange = isDateInRange(day);
              return (
                <Grid item xs={12/7} key={day}>
                  <Button
                    onClick={() => handleDateClick(day)}
                    sx={{
                      minWidth: 36,
                      height: 36,
                      p: 0,
                      borderRadius: 1.5,
                      fontSize: "0.813rem",
                      fontWeight: (isStart || isEnd) ? 700 : 500,
                      bgcolor: (isStart || isEnd) ? "primary.main" : inRange ? "rgba(25, 118, 210, 0.08)" : "transparent",
                      color: (isStart || isEnd) ? "primary.contrastText" : inRange ? "primary.main" : "text.primary",
                      "&:hover": { bgcolor: (isStart || isEnd) ? "primary.dark" : "action.hover" }
                    }}
                  >
                    {day}
                  </Button>
                </Grid>
              );
            })}
          </Grid>

          <Stack direction="row" spacing={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleApplyDates}
              sx={{ 
                py: 1.25, 
                borderRadius: 2, 
                fontWeight: 600,
                fontSize: "0.875rem",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                textTransform: "none"
              }}
            >
              Aplicar Filtro
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearDates}
              sx={{ 
                py: 1.25, 
                borderRadius: 2, 
                fontWeight: 600,
                fontSize: "0.875rem",
                textTransform: "none"
              }}
            >
              Limpar
            </Button>
          </Stack>
        </Box>
      </Menu>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Reports Component
// ---------------------------------------------------------------------------
const Reports = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();
  const history = useHistory();
  const { getReport } = useDashboard();
  const { user } = useContext(AuthContext);

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [queueIds, setQueueIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [options, setOptions] = useState([]);
  const [dateFrom, setDateFrom] = useState(moment("1", "D").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [onlyRated, setOnlyRated] = useState(false);
  const [totalTickets, setTotalTickets] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });

  useEffect(() => {
    if (!searchParam) {
      setOptions([]);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  const exportarGridParaExcel = async () => {
    setLoading(true);
    try {
      const data = await getReport({
        searchParam,
        contactId: selectedContactId,
        whatsappId: JSON.stringify(selectedWhatsapp),
        users: JSON.stringify(userIds),
        queueIds: JSON.stringify(queueIds),
        status: JSON.stringify(selectedStatus),
        dateFrom,
        dateTo,
        page: 1,
        pageSize: 9999999,
        onlyRated: onlyRated ? "true" : "false"
      });

      const ticketsData = data.tickets.map(ticket => {
        const createdAt = new Date(ticket.createdAt);
        const closedAt = new Date(ticket.closedAt);
        const dataFechamento = closedAt.toLocaleDateString();
        const horaFechamento = closedAt.toLocaleTimeString();
        const dataCriacao = createdAt.toLocaleDateString();
        const horaCriacao = createdAt.toLocaleTimeString();

        return {
          id: ticket.id,
          Conexão: ticket.whatsappName,
          Contato: ticket.contactName,
          Usuário: ticket.userName,
          Fila: ticket.queueName,
          Status: ticket.status,
          ÚltimaMensagem: ticket.lastMessage,
          DataAbertura: dataCriacao,
          HoraAbertura: horaCriacao,
          DataFechamento: ticket.closedAt === null ? "" : dataFechamento,
          HoraFechamento: ticket.closedAt === null ? "" : horaFechamento,
          TempoDeAtendimento: ticket.supportTime,
          nps: ticket.NPS,
        }
      });

      const ws = XLSX.utils.json_to_sheet(ticketsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'RelatorioDeAtendimentos');
      XLSX.writeFile(wb, 'relatorio-de-atendimentos.xlsx');
      toast.success("Relatório exportado com sucesso!");

      setPageNumber(pageNumber);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async (pageNumber) => {
    setLoading(true);
    try {
      const data = await getReport({
        searchParam,
        contactId: selectedContactId,
        whatsappId: JSON.stringify(selectedWhatsapp),
        users: JSON.stringify(userIds),
        queueIds: JSON.stringify(queueIds),
        status: JSON.stringify(selectedStatus),
        dateFrom,
        dateTo,
        page: pageNumber,
        pageSize: pageSize,
        onlyRated: onlyRated ? "true" : "false"
      });

      setTotalTickets(data.count);
      setTickets(data.tickets);
      setHasMore(data.hasMore);
      setPageNumber(pageNumber);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectedWhatsapps = (selecteds) => {
    setSelectedWhatsapp(selecteds);
  };

  const handleSelectedStatus = (selecteds) => {
    setSelectedStatus(selecteds);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setUserIds(users);
  };

  const handleDateRangeChange = ({ startDate, endDate }) => {
    setDateFilter({ startDate, endDate });
    setDateFrom(moment(startDate).format("YYYY-MM-DD"));
    setDateTo(moment(endDate).format("YYYY-MM-DD"));
  };

  const renderContactAutocomplete = () => {
    return (
      <Autocomplete
        fullWidth
        options={options}
        loading={loading}
        size="small"
        getOptionLabel={(option) => option.name}
        onChange={(e, newValue) => {
          if (newValue?.id) {
            setSelectedContactId(newValue.id);
            setSelectedContact(newValue);
          } else {
            setSelectedContactId(null);
            setSelectedContact(null);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Buscar contato..."
            variant="outlined"
          />
        )}
      />
    );
  };

  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'open':
      case 'pending':
        return { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', border: 'rgba(245, 158, 11, 0.2)' };
      case 'closed':
        return { bg: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: 'rgba(16, 185, 129, 0.2)' };
      default:
        return { bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: 'rgba(59, 130, 246, 0.2)' };
    }
  };

  const getStatusLabel = (status) => {
    switch(status?.toLowerCase()) {
      case 'open': return 'Aberto';
      case 'pending': return 'Pendente';
      case 'closed': return 'Fechado';
      default: return status || '-';
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <Container maxWidth="xl">
        <ShowTicketLogModal
          isOpen={openTicketMessageDialog}
          handleClose={() => setOpenTicketMessageDialog(false)}
          ticketId={ticketOpen?.id}
        />

        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                {i18n.t("reports.title") || "Relatórios"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                Análise de atendimentos
              </Typography>
            </Box>
          </Stack>

          <DateRangePicker
            dateRange={dateFilter}
            onDateRangeChange={handleDateRangeChange}
          />
        </Stack>

        {/* Filtros */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                Filtros de Pesquisa
              </Typography>
              <Button
                onClick={exportarGridParaExcel}
                disabled={loading}
                startIcon={<SaveAlt />}
                variant="contained"
                sx={{
                  bgcolor: PRIMARY_MAIN,
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                  "&:hover": {
                    bgcolor: PRIMARY_DARK,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                  },
                  "&:disabled": {
                    opacity: 0.5
                  }
                }}
              >
                Exportar
              </Button>
            </Stack>
          </Box>

          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Stack spacing={1}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                    Contato
                  </Typography>
                  {renderContactAutocomplete()}
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack spacing={1}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                    Conexão
                  </Typography>
                  <WhatsappsFilter onFiltered={handleSelectedWhatsapps} />
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack spacing={1}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                    Status
                  </Typography>
                  <StatusFilter onFiltered={handleSelectedStatus} />
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack spacing={1}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                    Usuários
                  </Typography>
                  <UsersFilter onFiltered={handleSelectedUsers} />
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack spacing={1}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                    Filas
                  </Typography>
                  <QueueSelectCustom
                    selectedQueueIds={queueIds}
                    onChange={values => setQueueIds(values)}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack spacing={1}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                    Data Inicial
                  </Typography>
                  <TextField
                    type="date"
                    value={dateFrom}
                    variant="outlined"
                    fullWidth
                    size="small"
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack spacing={1}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                    Data Final
                  </Typography>
                  <TextField
                    type="date"
                    value={dateTo}
                    variant="outlined"
                    fullWidth
                    size="small"
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={onlyRated}
                      onChange={() => setOnlyRated(!onlyRated)}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                      Apenas Avaliados
                    </Typography>
                  }
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleFilter(pageNumber)}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap"
                  }}
                >
                  Filtrar
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Tabela */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden",
            mb: 3
          }}
        >
          <TableContainer sx={{ maxHeight: "60vh" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", bgcolor: "background.paper", letterSpacing: "0.5px" }}>
                    ID
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", bgcolor: "background.paper", letterSpacing: "0.5px" }}>
                    WhatsApp
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", bgcolor: "background.paper", letterSpacing: "0.5px" }}>
                    Contato
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", bgcolor: "background.paper", letterSpacing: "0.5px" }}>
                    Usuário
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", bgcolor: "background.paper", letterSpacing: "0.5px" }}>
                    Fila
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", bgcolor: "background.paper", letterSpacing: "0.5px" }}>
                    Status
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", bgcolor: "background.paper", letterSpacing: "0.5px" }}>
                    Última Msg
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", bgcolor: "background.paper", letterSpacing: "0.5px" }}>
                    Data Abertura
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", bgcolor: "background.paper", letterSpacing: "0.5px" }}>
                    Data Fechamento
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", bgcolor: "background.paper", letterSpacing: "0.5px" }}>
                    Tempo
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", bgcolor: "background.paper", letterSpacing: "0.5px" }}>
                    NPS
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", bgcolor: "background.paper", letterSpacing: "0.5px" }}>
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((ticket) => {
                  const statusColors = getStatusColor(ticket.status);
                  return (
                    <TableRow
                      key={ticket.id}
                      sx={{
                        "&:hover": {
                          bgcolor: "action.hover",
                          transition: "background-color 0.2s"
                        }
                      }}
                    >
                      <TableCell align="center" sx={{ fontSize: "0.875rem", fontWeight: 600 }}>{ticket.id}</TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.875rem" }}>{ticket?.whatsappName || '-'}</TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.875rem" }}>{ticket?.contactName || '-'}</TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.875rem" }}>{ticket?.userName || '-'}</TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.875rem" }}>{ticket?.queueName || '-'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusLabel(ticket.status)}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            bgcolor: statusColors.bg,
                            color: statusColors.color,
                            border: "1px solid",
                            borderColor: statusColors.border
                          }}
                        />
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.875rem", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {ticket?.lastMessage || '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem" }}>{ticket?.createdAt || '-'}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem" }}>{ticket?.closedAt || '-'}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem" }}>{ticket?.supportTime || '-'}</TableCell>
                      <TableCell align="center">
                        {ticket?.NPS ? (
                          <Chip
                            label={ticket.NPS}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              bgcolor: ticket.NPS >= 9 ? "rgba(16, 185, 129, 0.1)" : ticket.NPS >= 7 ? "rgba(245, 158, 11, 0.1)" : "rgba(239, 68, 68, 0.1)",
                              color: ticket.NPS >= 9 ? "#10b981" : ticket.NPS >= 7 ? "#f59e0b" : "#ef4444",
                              border: "1px solid",
                              borderColor: ticket.NPS >= 9 ? "rgba(16, 185, 129, 0.2)" : ticket.NPS >= 7 ? "rgba(245, 158, 11, 0.2)" : "rgba(239, 68, 68, 0.2)"
                            }}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Logs do Ticket">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setOpenTicketMessageDialog(true);
                                setTicketOpen(ticket);
                              }}
                              sx={{
                                color: "primary.main",
                                "&:hover": { bgcolor: "rgba(25, 118, 210, 0.08)" }
                              }}
                            >
                              <History fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Acessar Ticket">
                            <IconButton
                              size="small"
                              onClick={() => { history.push(`/tickets/${ticket.uuid}`) }}
                              sx={{
                                color: "success.main",
                                "&:hover": { bgcolor: "rgba(16, 185, 129, 0.08)" }
                              }}
                            >
                              <Forward fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {loading && <TableRowSkeleton avatar columns={12} />}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Paginação */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={10}>
              <Pagination
                count={Math.ceil(totalTickets / pageSize)}
                page={pageNumber}
                onChange={(event, value) => handleFilter(value)}
                color="primary"
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    "&.Mui-selected": {
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      "&:hover": {
                        bgcolor: "primary.dark"
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl
                size="small"
                variant="outlined"
                fullWidth
              >
                <InputLabel>
                  {i18n.t("tickets.search.ticketsPerPage") || "Por página"}
                </InputLabel>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(e.target.value);
                  }}
                  label={i18n.t("tickets.search.ticketsPerPage") || "Por página"}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default Reports;